// Adapted from https://www.npmjs.com/package/gbify-geojson
// Credits: https://www.npmjs.com/~robmurray17

import proj4 from 'proj4';

const OSGB36_DEF =
  '+proj=tmerc +lat_0=49 +lon_0=-2 +k=0.9996012717 +x_0=400000 +y_0=-100000 +ellps=airy' +
  '+towgs84=446.448,-125.157,542.060,0.1502,0.2470,0.8421,-20.4894 +units=m +no_defs no_defs';

const OSGB36 = proj4(OSGB36_DEF);

// GeoJSON is meant to be WGS84 (RFC 7946), but exports from desktop GIS still arrive carrying a `crs`
// member naming something else, and the coordinates are then in metres on a grid rather than degrees.
// proj4 only ships definitions for a couple of codes, so the ones we can convert are listed here.
// Anything not listed is rejected rather than guessed at: a wrong definition silently places the AOI
// in the wrong country, which is worse than refusing the file.
const PROJECTION_DEFS: Record<string, string> = {
  '27700': OSGB36_DEF, // British National Grid
  '4277':
    '+proj=longlat +ellps=airy +towgs84=446.448,-125.157,542.060,0.1502,0.2470,0.8421,-20.4894 +no_defs', // OSGB36 lat/lon
  '3857':
    '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +no_defs', // Web Mercator
  '900913':
    '+proj=merc +a=6378137 +b=6378137 +lat_ts=0 +lon_0=0 +x_0=0 +y_0=0 +k=1 +units=m +nadgrids=@null +no_defs', // legacy code for Web Mercator
  '3035':
    '+proj=laea +lat_0=52 +lon_0=10 +x_0=4321000 +y_0=3210000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs', // ETRS89 LAEA Europe
  '2157':
    '+proj=tmerc +lat_0=53.5 +lon_0=-8 +k=0.99982 +x_0=600000 +y_0=750000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs', // Irish Transverse Mercator
  '29903':
    '+proj=tmerc +lat_0=53.5 +lon_0=-8 +k=1.000035 +x_0=200000 +y_0=250000 +ellps=mod_airy +towgs84=482.5,-130.6,564.6,-1.042,-0.214,-0.631,8.15 +units=m +no_defs', // Irish Grid
  '32629': '+proj=utm +zone=29 +datum=WGS84 +units=m +no_defs', // WGS84 UTM 29N
  '32630': '+proj=utm +zone=30 +datum=WGS84 +units=m +no_defs', // WGS84 UTM 30N
  '32631': '+proj=utm +zone=31 +datum=WGS84 +units=m +no_defs', // WGS84 UTM 31N
  '25829': '+proj=utm +zone=29 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs', // ETRS89 UTM 29N
  '25830': '+proj=utm +zone=30 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs', // ETRS89 UTM 30N
  '25831': '+proj=utm +zone=31 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs', // ETRS89 UTM 31N
};

// Codes that already mean WGS84 lon/lat, so there is nothing to reproject.
const WGS84_CODES = ['4326', 'CRS84', '84'];

function isValidCoord(coord) {
  return coord && coord.length >= 2 && typeof coord[0] === 'number' && typeof coord[1] === 'number';
}

function traverseCoords(coordinates, callback) {
  if (isValidCoord(coordinates)) {
    return callback(coordinates);
  }

  return coordinates.map((coord) => traverseCoords(coord, callback));
}

function traverseGeoJson(geoJson, processLeafNode) {
  if (geoJson.crs) {
    delete geoJson.crs;
  }

  if (geoJson.type === 'Feature') {
    geoJson.geometry = traverseGeoJson(geoJson.geometry, processLeafNode);
  } else if (geoJson.type === 'FeatureCollection') {
    geoJson.features = geoJson.features.map((feature) => traverseGeoJson(feature, processLeafNode));
  } else if (processLeafNode) {
    processLeafNode(geoJson);
  }

  return geoJson;
}

function traverseAndReproject(geoJson, from, to, precision) {
  const reproject = proj4(from, to);

  return traverseGeoJson(geoJson, (leafNode) => {
    leafNode.coordinates = traverseCoords(leafNode.coordinates, (coords) =>
      reproject.forward(coords).map((p) => {
        return 1 * p.toFixed(precision);
      }),
    );
  });
}

const truncatePoint = (point: number) => {
  if (Array.isArray(point)) {
    return point.map((p) => truncatePoint(p));
  } else {
    return +point.toFixed(6);
  }
};

const compress = (geoJson) =>
  geoJson?.features?.map((f) => {
    f.geometry && (f.geometry.bbox = undefined);
    (f.geometry as any)?.coordinates?.forEach((coord) => {
      for (let i = 0; i < coord.length; i++) {
        try {
          if (Array.isArray(coord[i])) {
            coord[i] = coord[i]?.map(truncatePoint);
          } else if (!isNaN(coord[i])) {
            coord[i] = truncatePoint(coord[i]);
          }
        } catch (e) {
          console.warn('Error compressing coordinates', coord, e);
        }
      }
    });
    return f;
  });

// Reads the CRS a file declares. Handles the shapes seen in the wild: the URN form QGIS writes
// ('urn:ogc:def:crs:EPSG::27700'), the short form ('EPSG:27700'), the OGC name for WGS84
// ('urn:ogc:def:crs:OGC:1.3:CRS84') and the pre-2008 `{ type: 'EPSG', properties: { code } }` form.
// Returns the bare code when one can be found, otherwise the raw name so it can be quoted back to the
// user, and undefined when the file declares nothing at all.
// Written-out names, for the exports that give a label instead of a code. Only unambiguous ones are
// listed: a bare 'OSGB 1936' could be the grid (27700) or the geographic CRS (4277), so it is left to
// fail rather than resolved by coin toss. Note 'WGS84' alone means lon/lat, but 'WGS84 /
// Pseudo-Mercator' does not — which is why a substring test for WGS84 would be actively harmful.
const CRS_NAME_ALIASES: [string, string][] = [
  ['BRITISH NATIONAL GRID', '27700'],
  ['PSEUDO-MERCATOR', '3857'],
  ['WEB MERCATOR', '3857'],
  ['IRISH TRANSVERSE MERCATOR', '2157'],
  ['IRISH GRID', '29903'],
];

const declaredCrs = (geoJson: GeoJSON.FeatureCollection): string | undefined => {
  const crs = (geoJson as any)?.crs;
  const raw = crs?.properties?.name ?? crs?.properties?.code;
  if (raw === undefined || raw === null || raw === '') {
    return undefined;
  }

  const name = String(raw).trim().toUpperCase();

  // A trailing code is the most reliable signal and the form every GIS export uses, so it wins.
  const code = name.match(/(\d{4,6})\s*$/);
  if (code) {
    return code[1];
  }

  if (name.includes('CRS84') || name === 'WGS84' || name === 'WGS 84') {
    return '4326';
  }

  const alias = CRS_NAME_ALIASES.find(([needle]) => name.includes(needle));
  return alias ? alias[1] : name;
};

// Last resort when a file declares no CRS. Longitude and latitude cannot exceed 180, so anything
// larger is metres on a projected grid. Every format we accept is WGS84 by specification (KML, GPX)
// or already reprojected by its parser (shapefiles, via the .prj), which leaves hand-rolled GeoJSON —
// and in this product that is British National Grid often enough to be the working assumption.
const looksProjected = (geoJson): boolean => {
  const coordinates = geoJson?.features?.[0]?.geometry?.coordinates;
  const beyondLonLat = (coord) => (Array.isArray(coord) ? beyondLonLat(coord[0]) : (coord ?? 0) > 180);
  return beyondLonLat(coordinates);
};

// Trimming coordinate precision is a size optimisation, not a correctness step, so a failure here must
// not lose the user's AOI — it stays non-fatal, as it was when the caller wrapped this in a try/catch.
// `onError` lets the caller log with whatever context it has (this file has no fileName/fileType to
// give a logger); omit it and the failure is simply absorbed.
const compressQuietly = (geoJson, onError?: (error: unknown) => void) => {
  try {
    compress(geoJson);
  } catch (error) {
    onError?.(error);
  }
};

// Returns the collection in EPSG:4326, whatever it arrived as, truncated to 6 decimal places.
// Throws when the file declares a CRS that is not in PROJECTION_DEFS, so the caller can tell the user
// to re-export rather than silently surveying the wrong piece of land. `onCompressError` is passed
// straight through to compressQuietly — it has no bearing on reprojection, which always throws on
// failure rather than going through this callback.
const toWGS84IfNeeded = (geoJson: GeoJSON.FeatureCollection, onCompressError?: (error: unknown) => void) => {
  const crs = declaredCrs(geoJson);

  if (!crs) {
    if (!looksProjected(geoJson)) {
      compressQuietly(geoJson, onCompressError);
      return geoJson;
    }
    return traverseAndReproject(geoJson, OSGB36_DEF, proj4.WGS84, 6);
  }

  if (WGS84_CODES.includes(crs)) {
    // Drop the declaration as well, so nothing downstream re-reads it and the payload stays smaller.
    delete (geoJson as any).crs;
    compressQuietly(geoJson, onCompressError);
    return geoJson;
  }

  const projection = PROJECTION_DEFS[crs];
  if (!projection) {
    const label = /^\d+$/.test(crs) ? `EPSG:${crs}` : crs;
    throw new Error(
      `This file is in ${label}, which cannot be converted automatically. ` +
        'Please re-export it as WGS84 (EPSG:4326) or British National Grid (EPSG:27700).',
    );
  }

  return traverseAndReproject(geoJson, projection, proj4.WGS84, 6);
};

export const coordinatesUtils = {
  toOSGB36: (geoJson) => traverseAndReproject(geoJson, proj4.WGS84, OSGB36, 2),
  toWGS84: (geoJson) => traverseAndReproject(geoJson, OSGB36, proj4.WGS84, 6),
  toWGS84IfNeeded,
  declaredCrs,
  compress,
  truncatePoint,
};
